import React from 'react'
import { Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import { Outlet, useLocation, Link as RouterLink, useOutletContext } from 'react-router-dom'
import { usePageTitle } from '../components/common/PageTitleContext'
import type { AppOutletContext } from '../app/AppShell'

type SuperadminTab = {
  value: string
  label: string
  description?: string
}

const SUPERADMIN_TABS: SuperadminTab[] = [
  {
    value: '/superadmin/stations-users/subscriptions',
    label: 'Subscriptions',
    description: 'Review station billing, seats, and credit policy settings.',
  },
  {
    value: '/superadmin/stations-users/grants',
    label: 'Manual grants',
    description: 'Grant one-off entitlements or credits for specific users or stations.',
  },
]

const SuperadminStationsUsers: React.FC = () => {
  const appContext = useOutletContext<AppOutletContext>()
  const { includeSuperadmin } = appContext
  const location = useLocation()

  usePageTitle('Superadmin - Stations & Users')

  const activeTab = React.useMemo(() => {
    return SUPERADMIN_TABS.find((tab) => location.pathname.startsWith(tab.value)) ?? SUPERADMIN_TABS[0]
  }, [location.pathname])

  if (!includeSuperadmin) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Typography variant="h5" component="h1">
            Access denied
          </Typography>
          <Typography color="text.secondary">
            The superadmin console is limited to users with the superadmin or WordPress administrator capability.
          </Typography>
        </Stack>
      </Paper>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          Superadmin - Stations & Users
        </Typography>
        <Typography color="text.secondary">
          Manage station subscriptions, admins, and manual grants for users.
        </Typography>
      </Stack>
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Stack spacing={0}>
          <Tabs
            value={activeTab.value}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Superadmin stations and users tabs"
            sx={{ px: 1 }}
          >
            {SUPERADMIN_TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                component={RouterLink}
                to={tab.value}
              />
            ))}
          </Tabs>
          {activeTab.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 2 }}>
              {activeTab.description}
            </Typography>
          ) : null}
        </Stack>
      </Paper>
      <Outlet context={appContext} />
    </Stack>
  )
}

export default SuperadminStationsUsers
